import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { uploadFile } from "@/components/UploadButton";
import { useT } from "@/i18n";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { ClipboardList, Loader2, Paperclip, Check } from "lucide-react";
import { toast } from "sonner";

export default function ShiftReportDialog({ open, onClose, channelId, onSubmitted }) {
  const { t } = useT();
  const [templates, setTemplates] = useState([]);
  const [tpl, setTpl] = useState(null);
  const [answers, setAnswers] = useState({});
  const [atts, setAtts] = useState({});
  const [busy, setBusy] = useState(false);
  const [uploadingQ, setUploadingQ] = useState(null);

  useEffect(() => {
    if (open) {
      setTpl(null); setAnswers({}); setAtts({});
      api.get("/shift-reports/templates").then((r) => {
        setTemplates(r.data);
        if (r.data.length === 1) setTpl(r.data[0]);
      });
    }
  }, [open]);

  const setVal = (qid, v) => setAnswers((a) => ({ ...a, [qid]: v }));
  const toggleCheckbox = (qid, opt) => setAnswers((a) => {
    const cur = Array.isArray(a[qid]) ? a[qid] : [];
    return { ...a, [qid]: cur.includes(opt) ? cur.filter((x) => x !== opt) : [...cur, opt] };
  });

  const handleUpload = async (qid, fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    setUploadingQ(qid);
    for (const f of files) {
      try { const d = await uploadFile(f, "shift-report"); setAtts((a) => ({ ...a, [qid]: [...(a[qid] || []), { file_id: d.id, name: d.original_filename, content_type: d.content_type }] })); }
      catch { toast.error("Upload failed"); }
    }
    setUploadingQ(null);
  };

  const submit = async () => {
    if (!tpl) return;
    setBusy(true);
    try {
      const payload = {
        template_id: tpl.id, channel_id: channelId,
        answers: tpl.questions.map((q) => ({ question_id: q.id, value: answers[q.id] ?? null, attachments: atts[q.id] || [] })),
      };
      await api.post("/shift-reports/reports", payload);
      toast.success(t("reportSubmitted"));
      onSubmitted && onSubmitted();
      onClose();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const renderInput = (q) => {
    const v = answers[q.id];
    switch (q.type) {
      case "long_text":
        return <Textarea rows={3} value={v || ""} onChange={(e) => setVal(q.id, e.target.value)} data-testid={`ans-${q.id}`} />;
      case "yesno":
        return (
          <div className="flex gap-2">
            {[t("yes"), t("no")].map((o) => (
              <Button key={o} type="button" size="sm" variant={v === o ? "default" : "outline"} onClick={() => setVal(q.id, o)} data-testid={`ans-${q.id}-${o}`}>{o}</Button>
            ))}
          </div>
        );
      case "multiple_choice":
        return (
          <div className="flex flex-wrap gap-2">
            {(q.options || []).map((o) => (
              <Button key={o} type="button" size="sm" variant={v === o ? "default" : "outline"} onClick={() => setVal(q.id, o)}>{o}</Button>
            ))}
          </div>
        );
      case "checkbox":
        return (
          <div className="flex flex-wrap gap-2">
            {(q.options || []).map((o) => {
              const on = Array.isArray(v) && v.includes(o);
              return <Button key={o} type="button" size="sm" variant={on ? "default" : "outline"} onClick={() => toggleCheckbox(q.id, o)}>{on && <Check className="w-3 h-3 mr-1" />}{o}</Button>;
            })}
          </div>
        );
      case "number":
        return <Input type="number" value={v || ""} onChange={(e) => setVal(q.id, e.target.value)} data-testid={`ans-${q.id}`} />;
      case "datetime":
        return <Input type="datetime-local" value={v || ""} onChange={(e) => setVal(q.id, e.target.value)} data-testid={`ans-${q.id}`} />;
      case "photo":
      case "document": {
        const accept = q.type === "photo" ? "image/*" : "image/*,application/pdf,.doc,.docx";
        return (
          <div className="flex items-center gap-2">
            <label className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-lg border border-border cursor-pointer hover:bg-accent">
              <Paperclip className="w-4 h-4" />{q.type === "photo" ? t("takePhoto") : t("attachFile")}
              <input type="file" accept={accept} multiple className="hidden" onChange={(e) => { handleUpload(q.id, e.target.files); e.target.value = ""; }} />
            </label>
            {uploadingQ === q.id && <Loader2 className="w-4 h-4 animate-spin" />}
            {(atts[q.id] || []).length > 0 && <span className="text-xs text-muted-foreground">{(atts[q.id] || []).length} {t("files")}</span>}
          </div>
        );
      }
      default:
        return <Input value={v || ""} onChange={(e) => setVal(q.id, e.target.value)} data-testid={`ans-${q.id}`} />;
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><ClipboardList className="w-5 h-5 text-primary" />{t("submitReport")}</DialogTitle>
        </DialogHeader>

        {templates.length === 0 ? (
          <p className="text-sm text-muted-foreground py-8 text-center">{t("noTemplates")}</p>
        ) : !tpl ? (
          <div className="space-y-2">
            <p className="text-sm text-muted-foreground">{t("selectTemplate")}</p>
            {templates.map((tp) => (
              <button key={tp.id} onClick={() => setTpl(tp)} data-testid={`pick-template-${tp.id}`}
                className="w-full text-left p-3 rounded-lg border border-border hover:bg-accent transition-colors">
                <p className="font-medium text-sm">{tp.name}</p>
                <p className="text-xs text-muted-foreground">{[tp.shift, tp.department].filter(Boolean).join(" · ")} · {tp.questions.length} Q</p>
              </button>
            ))}
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <p className="font-semibold">{tpl.name}</p>
              {templates.length > 1 && <Button size="sm" variant="ghost" onClick={() => setTpl(null)}>{t("selectTemplate")}</Button>}
            </div>
            {tpl.questions.map((q) => (
              <div key={q.id} className="space-y-1.5">
                <label className="text-sm font-medium">{q.label}{q.required && <span className="text-destructive"> *</span>}</label>
                {renderInput(q)}
              </div>
            ))}
            <DialogFooter>
              <Button variant="outline" onClick={onClose}>{t("cancel")}</Button>
              <Button onClick={submit} disabled={busy} data-testid="submit-shift-report">
                {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ClipboardList className="w-4 h-4 mr-2" />}{t("send")}
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
